import { CheckIcon, CloseIcon, DeleteIcon, EditIcon, LockIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import DeleteModal from 'components/Modal/DeleteModal';
import { ROLES } from 'helpers/enum';
import { useCreateOrganizationUser, useDeleteOrganizationUser } from 'queries/useOrganization';
import { useCreateUser, useDeleteUser } from 'queries/useUser';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ScrollableTable from './ScrollableTable';

interface CompanyUsersTabProps {
  users: any;
  organizationId: string;
  onUpdateUser?: (_userId: string, _userData: any) => void;
  onEditingStateChange?: (_isEditing: boolean) => void;
  isNotSuperAdmin?: boolean;
}

const CompanyUsersTab: React.FC<CompanyUsersTabProps> = ({
  users,
  organizationId,
  onUpdateUser,
  onEditingStateChange,
  isNotSuperAdmin = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // State for switch edit confirmation modal
  const {
    isOpen: isSwitchModalOpen,
    onOpen: onSwitchModalOpen,
    onClose: onSwitchModalClose,
  } = useDisclosure();
  const [pendingEditUser, setPendingEditUser] = useState<any>(null);

  // Ref for AlertDialog
  const switchModalRef = useRef<HTMLButtonElement>(null);

  // Delete user mutation hook
  const deleteUserMutation = useDeleteUser();
  const deleteUserMutationOrganization = useDeleteOrganizationUser();

  // Create user mutation hook
  const createUserMutation = useCreateUser();
  const createUserMutationOrganization = useCreateOrganizationUser();

  const colorText = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('0.5px solid rgb(209, 213, 219)', '1px solid');

  // Color mode values for input styling
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputColor = useColorModeValue('black', 'white');
  const inputBorderColor = useColorModeValue('gray.300', 'gray.500');
  const inputFocusBorderColor = useColorModeValue('blue.500', 'blue.300');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  // State for inline editing
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<any>({});
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // State for creating new user
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: ROLES.USER,
    locale: 'en',
  });
  const [newUserErrors, setNewUserErrors] = useState<{ [key: string]: string }>({});

  // State for tracking which user is being deleted
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // State for tracking which user is being updated
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  // Listen for add user trigger from parent
  useEffect(() => {
    const handleTriggerAddUser = () => {
      // If already editing or creating, show confirmation modal
      if (editingUser || isCreatingUser) {
        setPendingEditUser({ id: 'new', action: 'create' });
        onSwitchModalOpen();
        return;
      }

      setIsCreatingUser(true);
      setNewUserData({
        first_name: '',
        last_name: '',
        email: '',
        role: ROLES.USER,
        locale: 'en',
      });
      setNewUserErrors({});
    };

    window.addEventListener('triggerAddUser', handleTriggerAddUser);
    return () => {
      window.removeEventListener('triggerAddUser', handleTriggerAddUser);
    };
  }, [editingUser, isCreatingUser, onSwitchModalOpen]);

  // Notify parent about editing state changes
  useEffect(() => {
    if (onEditingStateChange) {
      onEditingStateChange(editingUser !== null || isCreatingUser);
    }
  }, [editingUser, isCreatingUser, onEditingStateChange]);

  // Available roles (excluding SUPER_ADMIN)
  const availableRoles = [
    { value: ROLES.USER, label: ROLES.USER },
    { value: ROLES.ADMIN, label: ROLES.ADMIN },
  ];

  // Available locales with translations
  const availableLocales = [
    { value: 'en', label: t('companyManagement.languageEnglish') },
    { value: 'ja', label: t('companyManagement.languageJapanese') },
  ];

  // Validation function for editing
  const validateFields = () => {
    const errors: { [key: string]: string } = {};

    if (!editingData.first_name || editingData.first_name.trim() === '') {
      errors.first_name = t('managementSystem.companyDetail.firstNameRequired');
    }

    if (!editingData.last_name || editingData.last_name.trim() === '') {
      errors.last_name = t('managementSystem.companyDetail.lastNameRequired');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validation function for new user
  const validateNewUser = () => {
    const errors: { [key: string]: string } = {};

    if (!newUserData.first_name || newUserData.first_name.trim() === '') {
      errors.first_name = t('managementSystem.companyDetail.firstNameRequired');
    }

    if (!newUserData.last_name || newUserData.last_name.trim() === '') {
      errors.last_name = t('managementSystem.companyDetail.lastNameRequired');
    }

    if (!newUserData.email || newUserData.email.trim() === '') {
      errors.email = t('managementSystem.companyDetail.emailRequired');
    } else {
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newUserData.email.trim())) {
        errors.email = t('managementSystem.companyDetail.emailInvalid');
      }
    }

    setNewUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle inline editing with confirmation if already editing
  const handleEdit = (user: any) => {
    // Prevent editing super admin
    if (user.role === ROLES.SUPER_ADMIN) {
      toast({
        title: t('common.error'),
        description: t('managementSystem.companyDetail.superAdminProtected'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // If creating a new user, show confirmation modal
    if (isCreatingUser) {
      setPendingEditUser(user);
      onSwitchModalOpen();
      return;
    }

    // If already editing a different user, show confirmation modal
    if (editingUser && editingUser !== user.id) {
      setPendingEditUser(user);
      onSwitchModalOpen();
      return;
    }

    // If editing the same user or no user is being edited, proceed normally
    startEditing(user);
  };

  // Start editing a user
  const startEditing = (user: any) => {
    setEditingUser(user.id);
    setEditingData({
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      locale: user.locale || 'en',
    });
    setFieldErrors({}); // Clear previous errors
  };

  // Confirm switch to edit different user or create new user
  const confirmSwitchEdit = () => {
    if (pendingEditUser) {
      if (pendingEditUser.action === 'create') {
        // Switch to creating new user
        setIsCreatingUser(true);
        setEditingUser(null);
        setNewUserData({
          first_name: '',
          last_name: '',
          email: '',
          role: ROLES.USER,
          locale: 'en',
        });
        setNewUserErrors({});
      } else {
        // Switch to editing existing user
        startEditing(pendingEditUser);
      }
      setPendingEditUser(null);
      onSwitchModalClose();
    }
  };

  // Cancel switch edit
  const cancelSwitchEdit = () => {
    setPendingEditUser(null);
    onSwitchModalClose();
  };

  const handleSave = async (userId: string) => {
    console.log('handleSave called for user:', userId, 'with data:', editingData);

    // Validate fields before saving
    if (!validateFields()) {
      // Show specific error messages for each field
      if (fieldErrors.first_name) {
        toast({
          title: t('common.error'),
          description: fieldErrors.first_name,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else if (fieldErrors.last_name) {
        toast({
          title: t('common.error'),
          description: fieldErrors.last_name,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      return;
    }

    try {
      // Set the user being updated
      setUpdatingUserId(userId);

      if (onUpdateUser) {
        console.log('Calling onUpdateUser...');
        const result = await onUpdateUser(userId, editingData);
        console.log('onUpdateUser completed successfully:', result);

        // Only show success toast when API call succeeds
        toast({
          title: t('common.success'),
          description: t('managementSystem.companyDetail.userUpdated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Reset editing state only on success
        setEditingUser(null);
        setEditingData({});
        setFieldErrors({});
      }
    } catch (error) {
      console.error('Error in handleSave:', error);

      // Error toast is handled elsewhere, just keep editing state for retry
      console.log('Keeping editing state due to error');
    } finally {
      // Clear the updating user state
      setUpdatingUserId(null);
    }
  };

  const handleCancel = () => {
    setEditingUser(null);
    setEditingData({});
    setFieldErrors({});
  };

  // Handle creating new user
  const handleCreateUser = async () => {
    console.log('handleCreateUser called with data:', newUserData);

    // Validate fields before creating
    if (!validateNewUser()) {
      // Show specific error messages for each field
      if (newUserErrors.first_name) {
        toast({
          title: t('common.error'),
          description: newUserErrors.first_name,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else if (newUserErrors.last_name) {
        toast({
          title: t('common.error'),
          description: newUserErrors.last_name,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else if (newUserErrors.email) {
        toast({
          title: t('common.error'),
          description: newUserErrors.email,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      return;
    }

    try {
      console.log('Calling createUserMutation...');
      const result = isNotSuperAdmin
        ? await createUserMutation.mutateAsync({
            userData: newUserData,
          })
        : await createUserMutationOrganization.mutateAsync({
            orgId: organizationId,
            userData: newUserData,
          });

      if (result.success === false && result.message) {
        return;
      }
      console.log('createUserMutation completed successfully:', result);

      // Show success toast
      toast({
        title: t('common.success'),
        description: t('managementSystem.companyDetail.userCreated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset creating state
      setIsCreatingUser(false);
      setNewUserData({
        first_name: '',
        last_name: '',
        email: '',
        role: ROLES.USER,
        locale: 'en',
      });
      setNewUserErrors({});
    } catch (error) {
      console.error('Error in handleCreateUser:', error);
      // Error toast is handled elsewhere, just keep creating state for retry
      console.log('Keeping creating state due to error');
    }
  };

  // Cancel creating new user
  const handleCancelCreate = () => {
    setIsCreatingUser(false);
    setNewUserData({
      first_name: '',
      last_name: '',
      email: '',
      role: ROLES.USER,
      locale: 'en',
    });
    setNewUserErrors({});
  };

  const handleDelete = (user: any) => {
    // Prevent deleting super admin
    if (user.role === ROLES.SUPER_ADMIN) {
      toast({
        title: t('common.error'),
        description: t('managementSystem.companyDetail.superAdminProtected'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUserToDelete(user);
    onOpen();
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        // Set the user being deleted
        setDeletingUserId(userToDelete.id);

        // Use user service API to delete user
        const result = isNotSuperAdmin
          ? await deleteUserMutation.mutateAsync({
              userId: userToDelete.id,
            })
          : await deleteUserMutationOrganization.mutateAsync({
              orgId: organizationId,
              userId: userToDelete.id,
            });

        if (result.success === false && result.message) {
          return;
        }

        toast({
          title: t('common.success'),
          description: t('managementSystem.companyDetail.userDeleted'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: t('common.error'),
          description: t('managementSystem.companyDetail.userDeleteFailed'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        // Clear the deleting user state
        setDeletingUserId(null);
      }
    }
    onClose();
    setUserToDelete(null);
  };

  // Prepare table data with new user row at the top
  const tableData = [
    // New user row (if creating)
    ...(isCreatingUser
      ? [
          {
            id: 'new',
            first_name: '',
            last_name: '',
            email: '',
            role: ROLES.USER,
            locale: 'en',
            isNewUser: true,
          },
        ]
      : []),
    // Existing users
    ...(users || []),
  ];

  // Render cell content based on state
  const renderCell = (user: any, field: string) => {
    // For new user row
    if (user.isNewUser) {
      const value = newUserData[field as keyof typeof newUserData];
      const error = newUserErrors[field];

      if (field === 'role') {
        return (
          <Select
            color={inputColor}
            bg={inputBg}
            border='1px solid'
            borderColor={error ? 'red.500' : inputBorderColor}
            _focus={{
              borderColor: inputFocusBorderColor,
              boxShadow: '0 0 0 1px ' + inputFocusBorderColor,
            }}
            _placeholder={{
              color: placeholderColor,
            }}
            isDisabled={createUserMutation.isPending}
            onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value as ROLES })}
            size='sm'
            value={value as string}
          >
            {availableRoles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
        );
      }

      if (field === 'locale') {
        return (
          <Select
            color={inputColor}
            bg={inputBg}
            border='1px solid'
            borderColor={error ? 'red.500' : inputBorderColor}
            _focus={{
              borderColor: inputFocusBorderColor,
              boxShadow: '0 0 0 1px ' + inputFocusBorderColor,
            }}
            _placeholder={{
              color: placeholderColor,
            }}
            isDisabled={createUserMutation.isPending}
            onChange={(e) => setNewUserData({ ...newUserData, locale: e.target.value })}
            size='sm'
            value={value as string}
          >
            {availableLocales.map((locale) => (
              <option key={locale.value} value={locale.value}>
                {locale.label}
              </option>
            ))}
          </Select>
        );
      }

      // Get placeholder based on field
      let placeholder = '';
      if (field === 'first_name') {
        placeholder = t('managementSystem.companyDetail.firstNamePlaceholder');
      } else if (field === 'last_name') {
        placeholder = t('managementSystem.companyDetail.lastNamePlaceholder');
      } else if (field === 'email') {
        placeholder = t('managementSystem.companyDetail.emailPlaceholder');
      }

      return (
        <Input
          color={inputColor}
          bg={inputBg}
          border='1px solid'
          borderColor={error ? 'red.500' : inputBorderColor}
          _focus={{
            borderColor: inputFocusBorderColor,
            boxShadow: '0 0 0 1px ' + inputFocusBorderColor,
          }}
          _placeholder={{
            color: placeholderColor,
          }}
          isDisabled={createUserMutation.isPending}
          onChange={(e) => {
            setNewUserData({ ...newUserData, [field]: e.target.value });
            // Clear error when user starts typing
            if (error) {
              setNewUserErrors((prev) => ({ ...prev, [field]: '' }));
            }
          }}
          placeholder={placeholder}
          size='sm'
          value={value as string}
        />
      );
    }

    // For existing users
    if (editingUser === user.id) {
      const value = editingData[field as keyof typeof editingData];
      const error = fieldErrors[field];

      // Email field is read-only when editing
      if (field === 'email') {
        return (
          <Text color={colorText} fontSize='sm'>
            {user.email}
          </Text>
        );
      }

      if (field === 'role') {
        return (
          <Select
            color={inputColor}
            bg={inputBg}
            border='1px solid'
            borderColor={error ? 'red.500' : inputBorderColor}
            _focus={{
              borderColor: inputFocusBorderColor,
              boxShadow: '0 0 0 1px ' + inputFocusBorderColor,
            }}
            _placeholder={{
              color: placeholderColor,
            }}
            isDisabled={updatingUserId === user.id}
            onChange={(e) => setEditingData({ ...editingData, role: e.target.value as ROLES })}
            size='sm'
            value={value as string}
          >
            {availableRoles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
        );
      }

      if (field === 'locale') {
        return (
          <Select
            color={inputColor}
            bg={inputBg}
            border='1px solid'
            borderColor={error ? 'red.500' : inputBorderColor}
            _focus={{
              borderColor: inputFocusBorderColor,
              boxShadow: '0 0 0 1px ' + inputFocusBorderColor,
            }}
            _placeholder={{
              color: placeholderColor,
            }}
            isDisabled={updatingUserId === user.id}
            onChange={(e) => setEditingData({ ...editingData, locale: e.target.value })}
            size='sm'
            value={value as string}
          >
            {availableLocales.map((locale) => (
              <option key={locale.value} value={locale.value}>
                {locale.label}
              </option>
            ))}
          </Select>
        );
      }

      // Get placeholder based on field
      let placeholder = '';
      if (field === 'first_name') {
        placeholder = t('managementSystem.companyDetail.firstNamePlaceholder');
      } else if (field === 'last_name') {
        placeholder = t('managementSystem.companyDetail.lastNamePlaceholder');
      }

      return (
        <Input
          color={inputColor}
          bg={inputBg}
          border='1px solid'
          borderColor={error ? 'red.500' : inputBorderColor}
          _focus={{
            borderColor: inputFocusBorderColor,
            boxShadow: '0 0 0 1px ' + inputFocusBorderColor,
          }}
          _placeholder={{
            color: placeholderColor,
          }}
          isDisabled={updatingUserId === user.id}
          onChange={(e) => {
            setEditingData({ ...editingData, [field]: e.target.value });
            // Clear error when user starts typing
            if (error) {
              setFieldErrors((prev) => ({ ...prev, [field]: '' }));
            }
          }}
          placeholder={placeholder}
          size='sm'
          value={value as string}
        />
      );
    }

    // Display value for non-editing users
    return (
      <Text color={colorText} fontSize='sm'>
        {user[field]}
      </Text>
    );
  };

  // Render role cell
  const renderRole = (user: any) => {
    if (user.isNewUser) {
      return renderCell(user, 'role');
    }

    if (editingUser === user.id) {
      return renderCell(user, 'role');
    }

    return (
      <Badge
        px={2}
        py={1}
        fontSize='xs'
        colorScheme={user.role === ROLES.ADMIN ? 'blue' : 'green'}
        variant='subtle'
      >
        {user.role}
      </Badge>
    );
  };

  // Render locale cell
  const renderLocaleCell = (user: any) => {
    if (user.isNewUser) {
      return renderCell(user, 'locale');
    }

    if (editingUser === user.id) {
      return renderCell(user, 'locale');
    }

    // Display locale with flag for non-editing users
    const locale = user.locale || 'en';
    const getFlag = (loc: string) => {
      if (loc === 'ja') return '🇯🇵';
      return '🇬🇧';
    };

    const getLabel = (loc: string) => {
      if (loc === 'ja') return t('companyManagement.languageJapanese');
      return t('companyManagement.languageEnglish');
    };

    return (
      <HStack spacing={2}>
        <Text color={colorText} fontSize='sm'>
          {getFlag(locale)}
        </Text>
        <Text color={colorText} fontSize='sm'>
          {getLabel(locale)}
        </Text>
      </HStack>
    );
  };

  // Render actions cell
  const renderActions = (user: any) => {
    if (user.isNewUser) {
      return (
        <HStack spacing={2}>
          <Tooltip label={t('common.save')}>
            <IconButton
              aria-label={t('common.save')}
              colorScheme='green'
              icon={<CheckIcon />}
              isLoading={createUserMutation.isPending}
              onClick={handleCreateUser}
              size='sm'
              variant='solid'
            />
          </Tooltip>
          <Tooltip label={t('common.cancel')}>
            <IconButton
              aria-label={t('common.cancel')}
              colorScheme='gray'
              icon={<CloseIcon />}
              isDisabled={createUserMutation.isPending}
              onClick={handleCancelCreate}
              size='sm'
              variant='solid'
            />
          </Tooltip>
        </HStack>
      );
    }

    if (editingUser === user.id) {
      return (
        <HStack spacing={2}>
          <Tooltip label={t('common.save')}>
            <IconButton
              aria-label={t('common.save')}
              colorScheme='green'
              icon={<CheckIcon />}
              isDisabled={updatingUserId === user.id}
              isLoading={updatingUserId === user.id}
              onClick={() => handleSave(user.id)}
              size='sm'
              variant='solid'
            />
          </Tooltip>
          <Tooltip label={t('common.cancel')}>
            <IconButton
              aria-label={t('common.cancel')}
              colorScheme='gray'
              icon={<CloseIcon />}
              isDisabled={updatingUserId === user.id}
              onClick={handleCancel}
              size='sm'
              variant='solid'
            />
          </Tooltip>
        </HStack>
      );
    }

    // Check if user is super admin - if yes, don't show edit/delete buttons
    if (user.role === ROLES.SUPER_ADMIN) {
      return (
        <HStack spacing={2}>
          <Tooltip label={t('managementSystem.companyDetail.superAdminProtected')}>
            <IconButton
              aria-label={t('managementSystem.companyDetail.superAdminProtected')}
              colorScheme='purple'
              icon={<LockIcon />}
              isDisabled={true}
              size='sm'
              variant='solid'
            />
          </Tooltip>
        </HStack>
      );
    }

    return (
      <HStack spacing={2}>
        <Tooltip label={t('common.edit')}>
          <IconButton
            aria-label={t('common.edit')}
            colorScheme='blue'
            icon={<EditIcon />}
            onClick={() => handleEdit(user)}
            size='sm'
            variant='solid'
          />
        </Tooltip>
        <Tooltip label={t('common.delete')}>
          <IconButton
            aria-label={t('common.delete')}
            colorScheme='red'
            icon={<DeleteIcon />}
            isDisabled={deletingUserId === user.id}
            isLoading={deletingUserId === user.id}
            onClick={() => handleDelete(user)}
            size='sm'
            variant='solid'
          />
        </Tooltip>
      </HStack>
    );
  };

  // Table columns configuration
  const columns = [
    {
      title: t('managementSystem.companyDetail.firstName'),
      dataIndex: 'first_name',
      key: 'first_name',
      width: '140px',
      render: (row: any) => renderCell(row, 'first_name'),
    },
    {
      title: t('managementSystem.companyDetail.lastName'),
      dataIndex: 'last_name',
      key: 'last_name',
      width: '140px',
      render: (row: any) => renderCell(row, 'last_name'),
    },
    {
      title: t('managementSystem.companyDetail.email'),
      dataIndex: 'email',
      key: 'email',
      width: '230px',
      render: (row: any) => renderCell(row, 'email'),
    },
    {
      title: t('managementSystem.companyDetail.role'),
      dataIndex: 'role',
      key: 'role',
      width: '120px',
      render: (row: any) => renderRole(row),
    },
    {
      title: t('companyManagement.locale'),
      dataIndex: 'locale',
      key: 'locale',
      width: '120px',
      render: (row: any) => renderLocaleCell(row),
    },
    {
      title: t('common.actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: '120px',
      render: (row: any) => renderActions(row),
    },
  ];

  return (
    <VStack align='stretch' h='90%' spacing={4}>
      {/* Users Table */}
      <Box overflow='hidden' h='400px' border={borderColor} borderRadius='md'>
        <ScrollableTable
          data={tableData}
          columns={columns}
          rowKey='id'
          noDataText={t('managementSystem.companyDetail.noData')}
        />
      </Box>

      {/* Switch Edit Confirmation Modal */}
      <AlertDialog
        isOpen={isSwitchModalOpen}
        leastDestructiveRef={switchModalRef}
        onClose={onSwitchModalClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              {pendingEditUser?.action === 'create'
                ? t('managementSystem.companyDetail.switchToCreateConfirmationTitle')
                : t('managementSystem.companyDetail.switchEditConfirmationTitle')}
            </AlertDialogHeader>
            <AlertDialogBody>
              {pendingEditUser?.action === 'create'
                ? t('managementSystem.companyDetail.switchToCreateConfirmationBody')
                : t('managementSystem.companyDetail.switchEditConfirmationBody', {
                    userName: pendingEditUser?.first_name,
                  })}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={confirmSwitchEdit}>{t('common.yes')}</Button>
              <Button colorScheme='red' onClick={cancelSwitchEdit}>
                {t('common.no')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={isOpen}
        onClose={onClose}
        recordDelete={userToDelete}
        action={confirmDelete}
        dataHeader={t('managementSystem.companyDetail.deleteUser')}
        dataBody={t('managementSystem.companyDetail.deleteUserConfirm')}
        btnAction={t('common.delete')}
        color={'red'}
        isLoading={deleteUserMutation.isPending}
        loadingText={t('common.deleting')}
      />
    </VStack>
  );
};

export default CompanyUsersTab;
